import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import catalogue from "./analyticsSearchCatalogue.json";

export function normalizeSearch(text) {
  return String(text || "").replace(/([a-z])([A-Z])/g, "$1 $2")
    .normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
const groups = [
  ["rm", "raw material"], ["qa", "qc", "quality", "antibiotic"],
  ["po", "purchase order", "orders"], ["dispatch", "shipment"],
  ["stock", "inventory", "cold store"], ["renewal", "renewals", "certificate", "certification"],
];
function searchText(item) {
  let text = normalizeSearch(`${item.title} ${item.tabLabel} ${item.keywords}`);
  for (const group of groups) {
    if (group.some(term => (` ${text} `).includes(` ${term} `))) text += " " + group.join(" ");
  }
  // The farm audit endpoint is displayed under its actual plant-audit title.
  if (item.target === "ceo-farm-audit-schedule") text += " farm audit schedule";
  return text;
}
const indexed = catalogue.map(item => ({ ...item, searchText: searchText(item) }));
function nearWord(term, word) {
  if (term.length < 4 || Math.abs(term.length - word.length) > 1) return false;
  if (term.length === word.length) {
    const diffs = [...term].map((c, i) => c === word[i] ? -1 : i).filter(i => i >= 0);
    return diffs.length <= 1 || (diffs.length === 2 && diffs[1] === diffs[0] + 1 &&
      term[diffs[0]] === word[diffs[1]] && term[diffs[1]] === word[diffs[0]]);
  }
  const [short, long] = term.length < word.length ? [term, word] : [word, term];
  for (let i = 0; i < long.length; i++) if (long.slice(0, i) + long.slice(i + 1) === short) return true;
  return false;
}
export function findAnalytics(query, allowAI = false) {
  const normalized = normalizeSearch(query).slice(0, 160);
  const terms = [...new Set(normalized.split(" ").filter(Boolean))];
  const items = indexed.filter(item => allowAI || item.tab !== "ai");
  if (!terms.length) return items.filter(item => item.kind === "Tab");
  return items.map(item => {
    const words = item.searchText.split(" ");
    const hits = terms.map(term => words.some(word => word.includes(term)) ? 2 : words.some(word => nearWord(term, word)) ? 1 : 0);
    const title = normalizeSearch(item.title);
    const matched = hits.filter(Boolean).length;
    return { ...item, partial: matched < terms.length,
      score: matched ? hits.reduce((a, b) => a + b, 0) * 10 +
        (matched === terms.length ? 100 : 0) + (title === normalized ? 200 : title.includes(normalized) ? 50 : 0) : 0 };
  }).filter(item => item.score > 0).sort((a, b) => b.score - a.score || a.title.localeCompare(b.title) || a.tabLabel.localeCompare(b.tabLabel));
}
export function analyticsDestination(item, location) {
  const params = new URLSearchParams(location.pathname === "/ceo-command-center" ? location.search : "");
  params.set("tab", item.tab);
  params.set("focus", item.target);
  return { pathname: "/ceo-command-center", search: "?" + params.toString() };
}
export function useAnalyticsSearchTarget(active, location) {
  const navigate = useNavigate();
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const requestedTab = searchParams.get("tab") || "overview";
    if (requestedTab !== active) return undefined;

    let target = searchParams.get("focus") || "";
    if (!target && location.hash.startsWith("#analytics-")) {
      try { target = decodeURIComponent(location.hash.slice(11)); }
      catch { return undefined; }
    }
    if (!target) return undefined;

    const catalogueItem = catalogue.find(item => item.tab === active && item.target === target);
    if (!catalogueItem) return undefined;

    let cancelled = false;
    let observer = null;
    let stopTimer = null;
    let highlightTimer = null;
    const retryTimers = [];

    const findTarget = () => {
      // Layout-only wrappers (display: contents) have no scroll/focus box.
      // Prefer the rendered widget when wrappers and children share a target.
      const hasBox = node => {
        const rect = node.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      };
      const exact = [...document.querySelectorAll("[data-analytics-target]")]
        .find(node => node.getAttribute("data-analytics-target") === target && hasBox(node));
      if (exact) return exact;

      const alias = [...document.querySelectorAll("[data-analytics-target-aliases]")]
        .find(node => hasBox(node) && (node.getAttribute("data-analytics-target-aliases") || "")
          .split(/\s+/).filter(Boolean).includes(target));
      if (alias) return alias;

      const byId = document.getElementById(target);
      if (byId && hasBox(byId)) return byId;

      // Last-resort global title lookup. Search titles are stable even when
      // internal widget IDs are refactored.
      const wanted = normalizeSearch(catalogueItem.title);
      const candidates = [
        ...document.querySelectorAll(".ceo-live-widget"),
        ...document.querySelectorAll(".ceo-module-heading"),
        ...document.querySelectorAll("[data-analytics-target]"),
      ];
      return candidates.find(node => {
        if (!hasBox(node)) return false;
        const titleNode = node.querySelector?.(".ceo-live-widget-title, .ceo-block-title");
        const label = normalizeSearch(titleNode?.textContent || node.getAttribute?.("aria-label") || "");
        return label && (label === wanted || label.includes(wanted) || wanted.includes(label));
      }) || null;
    };

    const scrollToTarget = (element, smooth = false) => {
      let scroller = element.parentElement;
      while (scroller && scroller !== document.body) {
        const style = window.getComputedStyle(scroller);
        const canScroll = /(auto|scroll)/.test(style.overflowY || "") &&
          scroller.scrollHeight > scroller.clientHeight + 8;
        if (canScroll) break;
        scroller = scroller.parentElement;
      }

      const behavior = smooth ? "smooth" : "auto";
      const offset = 130;
      if (!scroller || scroller === document.body || scroller === document.documentElement) {
        const rect = element.getBoundingClientRect();
        window.scrollTo({ top: Math.max(0, window.scrollY + rect.top - offset), behavior });
        return;
      }

      const er = element.getBoundingClientRect();
      const sr = scroller.getBoundingClientRect();
      scroller.scrollTo({
        top: Math.max(0, scroller.scrollTop + er.top - sr.top - offset),
        behavior,
      });
    };

    const reveal = (element, smooth = false) => {
      if (cancelled || !element) return false;
      const rect = element.getBoundingClientRect();
      if (!rect.width || !rect.height) return false;

      element.classList.add("analytics-search-highlight");
      element.style.scrollMarginTop = "150px";
      scrollToTarget(element, smooth);
      if (highlightTimer) window.clearTimeout(highlightTimer);
      highlightTimer = window.setTimeout(() => element.classList.remove("analytics-search-highlight"), 4000);

      if (smooth) {
        const oldTabIndex = element.getAttribute("tabindex");
        element.setAttribute("tabindex", "-1");
        try { element.focus({ preventScroll: true }); } catch { /* noop */ }
        if (oldTabIndex === null) element.removeAttribute("tabindex");
        else element.setAttribute("tabindex", oldTabIndex);
      }
      return true;
    };

    const lockOn = () => {
      if (cancelled) return false;
      const element = findTarget();
      if (!element) return false;
      // Navigate to the result once. Repeated delayed scrolls fought with the
      // user's own scrolling after the widget had already been found.
      if (!reveal(element, false)) return false;

      // Consume the focus instruction so later rerenders or location updates
      // cannot interpret it as another request to jump back to this widget.
      const params = new URLSearchParams(location.search);
      const hadFocus = params.has("focus");
      params.delete("focus");
      const clearSearch = params.toString();
      const clearHash = location.hash.startsWith("#analytics-") ? "" : location.hash;
      if (hadFocus || clearHash !== location.hash) {
        navigate({
          pathname: location.pathname,
          search: clearSearch ? `?${clearSearch}` : "",
          hash: clearHash,
        }, { replace: true, state: location.state });
      }
      return true;
    };

    if (!lockOn()) {
      observer = new MutationObserver(() => {
        if (lockOn() && observer) {
          observer.disconnect();
          observer = null;
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });

      // Poll as a fallback for components that become visible without a DOM insertion.
      const started = Date.now();
      const poll = () => {
        if (cancelled || Date.now() - started > 15000) return;
        if (lockOn()) {
          if (observer) observer.disconnect();
          observer = null;
          return;
        }
        retryTimers.push(window.setTimeout(poll, 200));
      };
      retryTimers.push(window.setTimeout(poll, 100));
    }

    stopTimer = window.setTimeout(() => {
      if (observer) observer.disconnect();
      observer = null;
    }, 15000);

    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
      if (stopTimer) window.clearTimeout(stopTimer);
      if (highlightTimer) window.clearTimeout(highlightTimer);
      retryTimers.forEach(id => window.clearTimeout(id));
      findTarget()?.classList.remove("analytics-search-highlight");
    };
  }, [active, location.hash, location.key, location.pathname, location.search, location.state, navigate]);
}
